import axios from 'axios';
import { ACCESS_TOKEN, REFRESH_TOKEN } from './constants';
const apiEndpoint = process.env.REACT_APP_API_URL;

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL
});

// Oturum düştüğünde nereye gidileceği router üzerinden çözülüyor.
//
// Burası eskiden `window.location.href` atıyordu: bu, uygulamayı baştan
// yükleyen tam bir belge navigasyonu. `/login` de landing sayfasını render
// ettiği için, süresi dolmamış ama sunucunun kabul etmediği bir access
// token'la gelen ziyaretçi şunu yaşıyordu — landing açılıyor, `/profile`e
// sıçrıyor, UserData 401 dönüyor, refresh de tutmuyor ve sayfa kendiliğinden
// yeniden yükleniyor. Ziyaretçinin gördüğü tek şey landing'in bir kez kendi
// kendine yenilenmesi. Router'la gezinildiğinde belge hiç yeniden yüklenmiyor.
//
// `App` mount olurken `setApiNavigator` ile kendi `navigate`ini veriyor. O
// olmadan (ilk istek router kurulmadan düşerse) `location.replace` yedeği
// devrede: yine tek bir sayfa yükü, ama geçmişe kayıt bırakmıyor.
let routerNavigate = null;

export const setApiNavigator = (fn) => { routerNavigate = fn; };

// Landing'i render eden yollar. Ziyaretçi zaten bunlardan birindeyse
// gidilecek bir yer yok; yine de gitmek, sayfayı sebepsiz yenileten şeyin ta
// kendisiydi.
const LANDING_PATHS = new Set(['/', '/login', '/register']);

const goTo = (path) => {
    if (LANDING_PATHS.has(path) && LANDING_PATHS.has(window.location.pathname)) return;
    if (routerNavigate) {
        routerNavigate(path, { replace: true });
        return;
    }
    window.location.replace(path);
};

api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem(ACCESS_TOKEN);
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            const isAuthRoute = originalRequest.url.includes('/api/token') || originalRequest.url.includes('/api/auth/google');
            if (isAuthRoute) {
                return Promise.reject(error);
            }
            originalRequest._retry = true;
            try {
                const refreshToken = localStorage.getItem(REFRESH_TOKEN);
                const res = await axios.post(`${apiEndpoint}/api/token/refresh/`, {
                    refresh: refreshToken,
                });

                if (res.status === 200) {
                    localStorage.setItem(ACCESS_TOKEN, res.data.access);
                    api.defaults.headers.common['Authorization'] = `Bearer ${res.data.access}`;
                    originalRequest.headers['Authorization'] = `Bearer ${res.data.access}`;
                    return api(originalRequest);
                }
            } catch (err) {
                localStorage.removeItem(ACCESS_TOKEN);
                localStorage.removeItem(REFRESH_TOKEN);
                goTo("/");
                return Promise.reject(err);
            }
        }
        // `error.response` yok demek: istek sunucuya hiç ulaşmadı (sunucu
        // kapalı, CORS, ağ kesildi, istek iptal edildi). Bunlar `.status`
        // okununca interceptor'ın kendisini TypeError ile düşürüyordu ve
        // çağıran taraf gerçek sebep yerine o hatayı görüyordu.
        if (error.response?.status === 409) {
            goTo("/settings");
            alert("Your subscription has expired, please renew it.");
            return Promise.reject(error);
        }
        if (error.response?.status === 403) {
            let lang = 'en';
            if (navigator.language && navigator.language.toLowerCase().startsWith('tr')) {
                lang = 'tr';
            }
            const existingAlert = document.getElementById('pay-alert-banner');
            if (!existingAlert) {
                const alertDiv = document.createElement('div');
                alertDiv.id = 'pay-alert-banner';
                alertDiv.className = "fixed top-4 left-0 right-0 mx-auto w-max max-w-[90vw] text-center z-[1100] bg-red-500 text-white px-6 py-3 rounded-xl shadow-lg text-sm font-medium animate-bounce";

                const translationMessage = lang === 'tr'
                    ? "Bu özelliği kullanmak için ödeme yapmalısınız. Lütfen Ayarlar → Ödemeler'e gidin"
                    : "You must pay to use this feature. Please go to Settings → Payments";

                alertDiv.innerText = translationMessage;
                document.body.appendChild(alertDiv);

                setTimeout(() => {
                    if (document.body.contains(alertDiv)) {
                        document.body.removeChild(alertDiv);
                    }
                }, 5000);
            }
            return Promise.reject(error);
        }
        return Promise.reject(error);
    }
);
export default api;